import { CorrelatedLogger } from '@eluve/logger';

import type { MuteDetector } from './MuteDetector';

/**
 * Configuration for creating a MuteDetectionPipeline
 */
export interface MuteDetectionPipelineConfig {
  mediaStream: MediaStream;
  audioContext?: AudioContext;
  MuteDetector: typeof MuteDetector;
  onMute: () => void;
  onUnmute: () => void;
  onSilence?: () => void;
  onSound?: () => void;
  logger?: CorrelatedLogger;
}

/**
 * MuteDetectionPipeline - Encapsulates AudioContext + AnalyserNode + MuteDetector setup
 *
 * This class provides a reusable way to set up mute detection for a MediaStream.
 * It handles:
 * - Creating or reusing an AudioContext
 * - Setting up an AnalyserNode for frequency analysis
 * - Creating and managing a MuteDetector instance
 * - Proper cleanup of all resources
 *
 * Usage:
 * ```typescript
 * const pipeline = new MuteDetectionPipeline({
 *   mediaStream,
 *   MuteDetector,
 *   onMute: () => console.log('OS/browser muted'),
 *   onUnmute: () => console.log('OS/browser unmuted'),
 *   onSilence: () => console.log('audio silent'),
 *   onSound: () => console.log('audio detected'),
 * });
 * pipeline.start();
 * // ... later
 * await pipeline.stop();
 * ```
 */
export class MuteDetectionPipeline {
  private audioContext: AudioContext | null = null;
  private ownsAudioContext = false;
  private analyser: AnalyserNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private muteDetector: InstanceType<typeof MuteDetector> | null = null;

  constructor(private config: MuteDetectionPipelineConfig) {}

  /**
   * Start the mute detection pipeline
   * Creates/reuses AudioContext, sets up analyser, and starts MuteDetector
   */
  start(): void {
    try {
      // Determine AudioContext: use provided or create new
      if (this.config.audioContext) {
        this.audioContext = this.config.audioContext;
        this.ownsAudioContext = false;
      } else {
        this.audioContext = new AudioContext();
        this.ownsAudioContext = true;
      }

      // Create AnalyserNode with standard FFT size for mute detection
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;

      // Create MediaStreamAudioSourceNode and connect to analyser
      this.sourceNode = this.audioContext.createMediaStreamSource(
        this.config.mediaStream,
      );
      this.sourceNode.connect(this.analyser);

      // Create MuteDetector instance
      this.muteDetector = new this.config.MuteDetector(
        this.analyser,
        () =>
          this.config.mediaStream
            ?.getAudioTracks()
            .every((track) => track.muted) ?? false,
      );

      // Attach event listeners
      this.muteDetector.addEventListener('mute', this.config.onMute);
      this.muteDetector.addEventListener('unmute', this.config.onUnmute);

      if (this.config.onSilence) {
        this.muteDetector.addEventListener('silence', this.config.onSilence);
      }

      if (this.config.onSound) {
        this.muteDetector.addEventListener('sound', this.config.onSound);
      }

      // Start monitoring
      this.muteDetector.start();

      this.log('info', 'Mute detection pipeline started successfully');
    } catch (error) {
      this.log('warn', 'Failed to start mute detection pipeline', { error });
    }
  }

  /**
   * Stop the mute detection pipeline and clean up resources
   * Closes AudioContext only if it was created by this pipeline
   */
  async stop(): Promise<void> {
    this.log('info', 'Stopping mute detection pipeline');

    // Stop and clean up MuteDetector
    if (this.muteDetector) {
      try {
        this.muteDetector.stop();
        this.muteDetector.removeEventListener('mute', this.config.onMute);
        this.muteDetector.removeEventListener('unmute', this.config.onUnmute);

        if (this.config.onSilence) {
          this.muteDetector.removeEventListener(
            'silence',
            this.config.onSilence,
          );
        }

        if (this.config.onSound) {
          this.muteDetector.removeEventListener('sound', this.config.onSound);
        }

        this.muteDetector = null;
      } catch (error) {
        this.log('warn', 'Error cleaning up MuteDetector', { error });
      }
    }

    // Disconnect source node
    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect();
        this.sourceNode = null;
      } catch (error) {
        this.log('warn', 'Error disconnecting source node', { error });
      }
    }

    // Clear analyser reference
    this.analyser = null;

    // Close AudioContext only if we created it
    if (this.ownsAudioContext && this.audioContext) {
      try {
        await this.audioContext.close();
      } catch (error) {
        this.log('warn', 'Error closing AudioContext', { error });
      }
    }

    this.audioContext = null;
    this.ownsAudioContext = false;

    this.log('info', 'Mute detection pipeline stopped');
  }

  /**
   * Get the AnalyserNode (for advanced use cases)
   */
  getAnalyser(): AnalyserNode | null {
    return this.analyser;
  }

  /**
   * Get the AudioContext (for advanced use cases)
   */
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /**
   * Internal logging helper
   */
  private log(
    level: 'info' | 'warn' | 'error',
    message: string,
    meta?: Record<string, unknown>,
  ): void {
    if (this.config.logger) {
      this.config.logger(level, message, meta);
    }
  }
}
